import { store } from '../store/stencil-redux';
import { MapLayerState } from "../store/map/map.reducer";
import { MapLayerFactory } from "./map-layer-factory.service";
import { COREO_LAYER_ID_PREFIX, MapLayer } from "./maps/maps-base-layer";
import { MapGeoJSONLayer } from "./maps/maps-geojson-layer";
import { safelyMoveLayer } from "./maps/maps.utils";
import { CoreoMapLayer } from "../types";
import { getAppId, getAppMapLayer } from "../store/selectors";
import * as mapboxgl from 'mapbox-gl/esm';

export class MapLayerManagerService {

  private constructor() { }

  public static instance: MapLayerManagerService = new MapLayerManagerService();

  private mapLayers: Map<string, { layer: MapLayer; state: MapLayerState }> = new Map();
  private map: mapboxgl.Map;
  /** Set when applyMapState runs synchroniseLayers; refreshLayers no-ops if this !== current app id. */
  private lastSyncedProjectId: number | null = null;
  /** Configured base-layer order (bottom to top) from the last synchroniseLayers call. */
  private layerOrder: string[] = [];

  public setMap(map: mapboxgl.Map) {
    this.map = map;
  }

  public getMapLayerByLayerId(id: string) {
    for (const layer of this.mapLayers.values()) {
      if (layer.layer.hasLayer(id)) {
        return layer;
      }
    }
    return null;
  }

  async synchroniseLayers(layers: MapLayerState[], projectId: number): Promise<void> {
    const targetIds = new Set(layers.map(l => l.id));
    const currentIds = new Set(this.mapLayers.keys());
    this.layerOrder = layers.map(l => l.id);

    // Remove layers that are not present in the new state
    for (const id of currentIds) {
      if (!targetIds.has(id)) {
        const layerRef = this.mapLayers.get(id);
        if (layerRef) {
          layerRef.layer.remove();
          this.mapLayers.delete(id);
        }
      }
    }

    for (let i = 0; i < layers.length; i++) {
      const layerState = layers[i];
      let existing = this.mapLayers.get(layerState.id);
      if (layerState.enabled) {
        if (typeof existing === 'undefined') {
          const newLayer = await MapLayerFactory.instance.createMapLayer(layerState);
          // Layers that create their Mapbox layer later (e.g. raster tiles once the
          // device reconnects) land on top of the stack; put them back in order.
          newLayer.onLayersChanged = () => this.positionLayer(layerState.id);
          existing = {
            layer: newLayer,
            state: layerState
          };
          this.mapLayers.set(layerState.id, existing);
          await newLayer.addTo(this.map);
        } else {
          await existing.layer.addTo(this.map);
          existing.layer.show();
        }
        existing.layer.move();
      } else {
        existing?.layer?.hide();
      }
    }

    this.lastSyncedProjectId = projectId;
  }

  /**
   * Move a base layer back to its configured position relative to the other base
   * layers, without disturbing anything stacked above them (data layers, custom
   * element layers). Used when a layer creates its Mapbox layer after the ordering
   * pass in synchroniseLayers has run.
   */
  positionLayer(id: string): void {
    const ref = this.mapLayers.get(id);
    const index = this.layerOrder.indexOf(id);
    if (!this.map || !ref || index === -1) {
      return;
    }

    const styleLayers = this.map.getStyle()?.layers ?? [];
    const styleIndex = (mapLayerId: string) => styleLayers.findIndex(l => l.id === mapLayerId);
    const ownIds = new Set(ref.layer.layerIds());
    const moveBefore = (beforeId: string) => {
      for (const mapLayerId of ref.layer.layerIds()) {
        safelyMoveLayer(this.map, mapLayerId, beforeId);
      }
    };

    const presentIds = (layerId: string): string[] => {
      const other = this.mapLayers.get(layerId);
      if (!other || other === ref) {
        return [];
      }
      return other.layer.layerIds()
        .filter(mapLayerId => typeof this.map.getLayer(mapLayerId) !== 'undefined')
        .sort((a, b) => styleIndex(a) - styleIndex(b));
    };

    // Nearest configured layer above this one that is on the map: slot in underneath it
    for (let i = index + 1; i < this.layerOrder.length; i++) {
      const [beforeId] = presentIds(this.layerOrder[i]);
      if (beforeId) {
        moveBefore(beforeId);
        return;
      }
    }

    // Otherwise sit directly above the nearest configured layer below this one
    for (let i = index - 1; i >= 0; i--) {
      const belowIds = presentIds(this.layerOrder[i]);
      if (belowIds.length === 0) {
        continue;
      }
      const beforeId = styleLayers[styleIndex(belowIds[belowIds.length - 1]) + 1]?.id;
      // No layer above the anchor, or this layer already sits there: nothing to move
      if (beforeId && !ownIds.has(beforeId)) {
        moveBefore(beforeId);
      }
      return;
    }

    // No other base layer to anchor against: stay underneath the Coreo data/custom
    // overlays, which MapService adds after the base layers. Basemap style layers do
    // not carry the Coreo prefix, so the first non-base Coreo layer is the bottom of
    // that overlay stack.
    const overlay = styleLayers.find(l =>
      l.id.startsWith(COREO_LAYER_ID_PREFIX) && !ownIds.has(l.id) && this.getMapLayerByLayerId(l.id) === null
    );
    if (overlay) {
      moveBefore(overlay.id);
    }
  }

  async refreshLayers() {
    const state = store.getState();
    const appId = getAppId(state);
    if (this.lastSyncedProjectId !== appId) {
      return;
    }
    for (const [_id, layerRef] of this.mapLayers.entries()) {
      const { layer, state: layerState } = layerRef;
      if (layerState.layerType === 'collection') {
        layer.remove();
        const recreated = await MapLayerFactory.instance.createMapLayer(layerState);
        this.mapLayers.set(layerState.id, {
          layer: recreated,
          state: layerState
        });
        await recreated.addTo(this.map);
        if (!layerState.enabled) {
          recreated.hide();
        } else {
          recreated.show();
        }
        recreated.move();
        continue;
      }
      if (layer instanceof MapGeoJSONLayer) {
        const layerConfig: CoreoMapLayer = getAppMapLayer(layerRef.state.sourceId)(state);
        if (layerConfig) {
          layer.update(layerConfig);
        }
      }
    }
  }

}